import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { LabelChips } from '@/components/labels';
import { ProjectDot } from '@/components/ProjectDot';
import { Sheet } from '@/components/ui';
import { useLabelMap, useProjectMap, useTask } from '@/data/hooks';
import { formatLogged } from '@/lib/time';
import type { TimerApi } from '@/timer/useTimer';
import { GoalCombobox } from './GoalCombobox';

/** Phones (3c/2c): the goal collapses to one row above the ring and opens the same combobox as a bottom sheet. */
export function GoalRow({ api }: { api: TimerApi }) {
  const { timer, actions } = api;
  const [open, setOpen] = useState(false);
  const projects = useProjectMap();
  const labelMap = useLabelMap();
  const task = useTask(timer.taskId);
  const project = timer.projectId ? projects.get(timer.projectId) : undefined;
  const labels = [...new Set([...(task?.labelIds ?? []), ...(project?.labelIds ?? [])])].map((id) => labelMap.get(id)).filter((l) => !!l);
  const logged = task ? formatLogged(task.trackedSec, task.estimateSec) : null;

  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-card border border-border bg-card text-left cursor-pointer">
        <span className="min-w-0 flex-1">
          <span className={`block truncate text-row-title ${timer.goalText ? 'text-ink' : 'text-ink-2'}`}>{timer.goalText || 'What are you working on?'}</span>
          <span className="flex items-center gap-2 mt-[2px] text-meta-sm text-ink-2">
            {project && <span className="inline-flex items-center gap-[6px]"><ProjectDot project={project} />{project.name}</span>}
            {logged && <span className="font-mono">{logged.text}</span>}
            <LabelChips labels={labels} max={2} />
          </span>
        </span>
        <ChevronRight size={16} className="text-ink-3 shrink-0" />
      </button>
      <Sheet open={open} onOpenChange={setOpen} title="Goal for this session" description="Type to create a task, or pick a saved one.">
        <GoalCombobox goal={{ taskId: timer.taskId, projectId: timer.projectId, goalText: timer.goalText }} startOpen
          onChange={(g) => { void actions.chooseGoal(g); setOpen(false); }} />
      </Sheet>
    </>
  );
}
